import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../hooks/useTranslate';
import { validateFolderName, folderValidationMessage, FolderNameValidationResult } from '../utils/validation/folderValidation';


interface Folder {
    id: number;
    name: string;
    uuid?: string;
    createdAt?: string;
    dataObjectCount?: number;
}

interface CreateFolderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (folderName: string) => Promise<FolderNameValidationResult & { folder?: Folder }>;
    onCreateAndAddNewDataObject: (folder: Folder) => void;
    onCreated?: (folder: Folder) => void;
    existingFolderNames: string[];
    folders: Folder[];
    // Legacy props that we'll ignore but keep for compatibility
    availableDataSources?: any[];
    onAssignDataSources?: any;
}

// Enhanced styles for better visual appeal
const ENHANCED_STYLES = `
.enhanced-folder-modal {
  transform-origin: center;
  animation: modalSlideIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.enhanced-folder-modal.closing {
  animation: modalSlideOut 0.3s ease-in-out;
  transform: scale(0.95);
  opacity: 0;
}

@keyframes modalSlideIn {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@keyframes modalSlideOut {
  0% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
  100% {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
}

.folder-input-container {
  position: relative;
}

.folder-input {
  transition: all 0.3s ease;
}

.folder-input:focus + .input-highlight {
  opacity: 1;
  transform: scaleX(1);
}

.input-highlight {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #dc2626, #991b1b);
  opacity: 0;
  transform: scaleX(0);
  transition: all 0.3s ease;
}

.success-pulse {
  animation: successPulse 1.5s ease-out;
}

@keyframes successPulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
}

.stagger-animation {
  animation: slideInStagger 0.5s ease-out;
}

@keyframes slideInStagger {
  0% {
    opacity: 0;
    transform: translateX(-20px);
  }
  100% {
    opacity: 1;
    transform: translateX(0);
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleId = 'enhanced-folder-modal-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = ENHANCED_STYLES;
        document.head.appendChild(style);
    }
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
    isOpen,
    onClose,
    onCreate,
    onCreateAndAddNewDataObject,
    onCreated,
    existingFolderNames,
    folders
}) => {
    const [name, setName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [validation, setValidation] = useState<FolderNameValidationResult | null>(null);
    const [mounted, setMounted] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslate();

    useEffect(() => {
        if (isOpen) {
            setMounted(true);
            setIsClosing(false);
            setName('');
            setValidation(null);
            setSubmitting(false);
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen && !isClosing && !submitting) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isClosing, submitting]);

    // Prevent body scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            // Store current scroll position
            const scrollY = window.scrollY;
            // Prevent body scroll
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.top = `-${scrollY}px`;
            document.body.style.width = '100%';

            return () => {
                // Restore body scroll and position
                document.body.style.overflow = '';
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.width = '';
                // Restore scroll position
                window.scrollTo(0, scrollY);
            };
        }
    }, [isOpen]);

    const runValidation = (value: string) => {
        const v = validateFolderName(value, existingFolderNames);
        setValidation(v);
        return v;
    };

    const handleSubmit = async (e: React.FormEvent, addDataObject: boolean = false) => {
        e.preventDefault();
        const v = runValidation(name);
        if (!v.valid) return;
        setSubmitting(true);
        try {
            const result = await onCreate(name);
            if (result.valid && result.folder) {
                if (addDataObject) {
                    setMounted(false);
                    onClose();
                    onCreateAndAddNewDataObject(result.folder);
                } else {
                    onCreated?.(result.folder);
                    handleClose();
                }
            } else {
                setValidation(result as FolderNameValidationResult);
            }
        } catch (error) {
            console.error('[CreateFolderModal] Error in handleSubmit:', error);
            throw error;
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setMounted(false);
            onClose();
        }, 300);
    };

    if (!isOpen && !mounted) return null;

    const message = validation ? folderValidationMessage(validation) : null;
    const hasError = !!message;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-[1200ms] ease-in-out"
            onClick={() => { }} // Disabled backdrop click to prevent accidental closes
        >
            <div className={`absolute inset-0 transition-all duration-500 ease-out ${isClosing ? 'bg-black/0 backdrop-blur-0' : 'bg-black/60 backdrop-blur-sm'
                }`} />
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-folder-title"
                className={`enhanced-folder-modal relative bg-white rounded-2xl shadow-2xl transform transition-all duration-500 flex flex-col max-h-[calc(100vh-2rem)] w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl m-4 ${isClosing ? 'closing' : ''
                    } border border-gray-100`}
                style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)'
                }}
            >
                {/* Enhanced Header */}
                <div className="px-3 sm:px-6 py-4 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-red-50 to-white rounded-t-2xl">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gradient-to-br from-red-500 to-red-600 rounded-xl shadow-lg">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                            </div>
                            <div>
                                <h2 id="create-folder-title" className="text-xl font-bold text-gray-900">
                                    {t('Create New Folder')}
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t('Organize your data objects into meaningful groups')}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleClose}
                            disabled={submitting}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-colors disabled:opacity-50"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <form onSubmit={(e) => handleSubmit(e, false)} className="flex-1 flex flex-col overflow-hidden">
                    {/* Enhanced Content */}
                    <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 sm:py-6 space-y-6">
                        {/* Folder Name Input */}
                        <div className="space-y-3">
                            <label htmlFor="create-folder-name" className="block text-sm font-semibold text-gray-700">
                                {t('Folder Name')}
                            </label>
                            <div className="folder-input-container">
                                <input
                                    ref={inputRef}
                                    id="create-folder-name"
                                    type="text"
                                    value={name}
                                    onChange={(e) => { setName(e.target.value); runValidation(e.target.value); }}
                                    placeholder={t('Enter a descriptive folder name...')}
                                    className={`folder-input w-full px-4 py-3 rounded-xl border-2 text-gray-900 bg-white/50 backdrop-blur-sm placeholder-gray-400 focus:outline-none focus:ring-0 ${hasError
                                        ? 'border-red-300 focus:border-red-500'
                                        : name && validation?.valid
                                            ? 'border-green-300 focus:border-green-500 success-pulse'
                                            : 'border-gray-200 focus:border-red-500'
                                        }`}
                                    disabled={submitting}
                                    autoComplete="off"
                                    aria-invalid={hasError || undefined}
                                    aria-describedby={hasError ? 'create-folder-name-error' : undefined}
                                    style={{
                                        fontSize: '16px',
                                        background: 'rgba(255, 255, 255, 0.8)'
                                    }}
                                />
                                <div className="input-highlight"></div>
                            </div>
                            {hasError && (
                                <div className="flex items-center space-x-2 text-red-600 animate-pulse">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <p id="create-folder-name-error" className="text-sm font-medium">{message}</p>
                                </div>
                            )}
                            {name && validation?.valid && (
                                <div className="flex items-center space-x-2 text-green-600">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-sm font-medium">{t('Folder name is available!')}</p>
                                </div>
                            )}
                        </div>

                        {/* Enhanced Existing Folders */}
                        <div className="space-y-3">
                            <div className="flex items-center space-x-2">
                                <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                </svg>
                                <h3 className="text-sm font-semibold text-red-700">{t('Existing Folders')} ({folders.length})</h3>
                            </div>
                            {folders.length > 0 ? (
                                <div className="bg-gradient-to-r from-white to-red-50 rounded-xl p-6 border border-red-100">
                                    <div className="max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-red-300 scrollbar-track-red-50 hover:scrollbar-thumb-red-400">
                                        <div className="grid grid-cols-1 gap-4 pr-2">
                                            {folders.map((folder, index) => (
                                                <div
                                                    key={folder.id}
                                                    className="bg-white rounded-lg p-4 border border-red-100 hover:border-red-300 hover:shadow-sm transition-all duration-200"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center space-x-4">
                                                            <div className="p-3 bg-red-100 rounded-lg">
                                                                <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                                                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900 text-base">{folder.name}</p>
                                                            </div>
                                                        </div>
                                                        <div className="bg-red-50 text-red-700 px-3 py-1.5 rounded-lg text-sm font-medium">
                                                            {folder.dataObjectCount || 0} {t('files')}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-6">
                                    <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                                        <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                                        </svg>
                                    </div>
                                    <p className="text-gray-500 font-medium text-sm">{t('No folders created yet')}</p>
                                    <p className="text-xs text-gray-400 mt-1">{t('Create your first folder to organize your data!')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Enhanced Footer */}
                    <footer className="flex-shrink-0 px-3 sm:px-6 py-4 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-red-50 rounded-b-2xl">
                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={(e) => { if (submitting || !name.trim() || (!!validation && !validation.valid)) return; handleSubmit(e as any, true); }}
                                disabled={submitting || !name.trim() || (!!validation && !validation.valid)}
                                className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base text-white font-semibold rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                style={{
                                    background: 'linear-gradient(to right, #6b7280, #4b5563, #374151)',
                                    boxShadow: '0 4px 8px rgba(107, 114, 128, 0.2)'
                                }}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                <span>{submitting ? t('Creating...') : t('New Data Object')}</span>
                            </button>

                            <button
                                type="submit"
                                disabled={submitting || !name.trim() || (!!validation && !validation.valid)}
                                className="px-4 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base text-white font-semibold rounded-lg transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                                style={{
                                    background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                    boxShadow: '0 4px 8px rgba(220, 38, 38, 0.2)'
                                }}
                            >
                                {submitting ? (
                                    <>
                                        <div className="loader-red loader-sm"></div>
                                        <span>{t('Creating...')}</span>
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span>{t('Create Folder')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                        <div className="text-center mt-3 text-xs text-gray-500">
                            <p>{t('Tip: You can also add a new data object to this folder after creation')}</p>
                        </div>
                    </footer>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default CreateFolderModal;